import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LoadingSpinner } from '../components'
import { Card } from '../components/ui'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { Guideline, VolunteerGuidelinesMetaProps } from '../data/types'
import { ChevronLeftIcon, ChevronRightIcon, DocumentIcon } from '../icons'

type Props = {
    meta: VolunteerGuidelinesMetaProps
}

export const VolunteerGuidelines = ({ meta }: Props) => {
    const navigate = useNavigate()
    const [guidelines, setGuidelines] = useState<Guideline[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchGuidelines = async () => {
            setLoading(true)
            setError(null)
            try {
                const response = await fetch(`${API_BASE_URL}/volunteer/guideline`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                setGuidelines(await response.json())
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load guidelines')
            } finally {
                setLoading(false)
            }
        }
        fetchGuidelines()
    }, [])

    return (
        <div className="flex-1 container mx-auto px-4 py-8">
            <div className="mb-6">
                <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300">
                    <ChevronLeftIcon className="w-5 h-5 me-2" />
                    Back to Home
                </Link>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">{meta.title}</h1>

            {loading && (
                <div className="flex justify-center items-center min-h-[300px]">
                    <LoadingSpinner meta={LoadingSpinnerMeta} />
                </div>
            )}

            {error && (
                <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                    <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                    <p>{error}</p>
                </div>
            )}

            {!loading && !error && guidelines.length === 0 && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                    <p className="text-xl">{meta.noGuidelinesMessage}</p>
                </div>
            )}

            {!loading && !error && guidelines.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2">
                    {guidelines.map((guideline) => {
                        const moduleCount = guideline.content?.modules?.length ?? 0
                        return (
                            <Card
                                key={guideline.id}
                                className="hover:shadow-lg transition-shadow cursor-pointer"
                                onClick={() => navigate(`/volunteer/guidelines/${guideline.id}`, { state: { guideline } })}
                            >
                                <div className="p-5">
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                        {guideline.name}
                                    </h2>
                                    {guideline.description && (
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                                            {guideline.description}
                                        </p>
                                    )}
                                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                                        <span className="flex items-center gap-1">
                                            <DocumentIcon className="w-4 h-4" />
                                            {moduleCount} {meta.documentsLabel}
                                        </span>
                                        <ChevronRightIcon className="w-5 h-5" />
                                    </div>
                                </div>
                            </Card>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
